import type { EChartDataset } from "echart-table-library";
import { applyChartSort, compareByDayLabel } from "@/widgets/common/chartSort";
import type { ChartSort } from "@/shared/types";
import type { RateOfOperationData } from "../dashboardResponse";
import type { RunningStatusSharePoint } from "./types";

/**
 * Pure transformations (convention section 24): input → function → output.
 * No Redux, no API calls — the thunk feeds rows in, views read the result.
 */

/** Measure fields of the share dataset, in canonical stack order. */
export const SHARE_FIELDS = [
  "print",
  "adjustment",
  "cleaning",
  "notActive",
] as const;

export type ShareField = (typeof SHARE_FIELDS)[number];

/**
 * Zips the API's columnar `rate_of_operation` section into one row per
 * day (the shape the stacked bars consume). Arrays are aligned
 * index-by-index with `categories`; missing entries fall back to 0.
 */
export function mapRateOfOperationToPoints(
  data: RateOfOperationData,
): RunningStatusSharePoint[] {
  const { categories, series_data } = data;
  return categories.map((day, index) => ({
    day,
    print: series_data.print[index] ?? 0,
    adjustment: series_data.adjustment[index] ?? 0,
    cleaning: series_data.cleaning[index] ?? 0,
    notActive: series_data.not_active[index] ?? 0,
  }));
}

/** Chart dataset for the EChart wrapper — day category + one measure per status. */
export function buildShareDataset(points: RunningStatusSharePoint[]): EChartDataset {
  return { dimensions: ["day", ...SHARE_FIELDS], source: points };
}

/**
 * Client-side day sort (chart-sort plan, Phần 3.2): reorders the stacked
 * rows chronologically (`dd/MM/yyyy` labels parsed via `dayStartMs` — never
 * string order). The widget's default sort is the chronological order,
 * which renders the same visual row order as the old "no sort" behavior
 * (the inverse axis puts the oldest on top either way). Count is
 * unchanged, so the card height and the Y scroll stay stable.
 */
export function sortSharePoints(
  points: RunningStatusSharePoint[],
  sort: ChartSort,
): RunningStatusSharePoint[] {
  return applyChartSort(points, sort, {
    day: compareByDayLabel((point) => point.day),
  });
}

/** `51.84` → `"51.84%"` (two decimals, matching the spec's label format). */
export function formatPercent(value: number): string {
  return `${Number(value).toFixed(2)}%`;
}

interface AxisTooltipParam {
  /** Axis tooltip carries one entry per stacked series. */
  name?: string;
  marker?: string;
  seriesName?: string;
  value?: unknown;
}

/**
 * Axis tooltip (spec point 4): hovering a row lists the full percentage
 * of all four statuses for that day — including the zero shares. Content
 * is numeric (day + series name + percent), so nothing needs translating.
 */
export function buildShareTooltip(): (params: unknown) => string {
  return (params) => {
    const items = (Array.isArray(params) ? params : []) as AxisTooltipParam[];
    if (items.length === 0) {
      return "";
    }
    return [
      `<b>${items[0].name ?? ""}</b>`,
      ...items.map(
        ({ marker, seriesName, value }) =>
          `${marker ?? ""} ${seriesName ?? ""}: ${formatPercent(Number(value ?? 0))}`,
      ),
    ].join("<br/>");
  };
}
